import { ReactNode } from 'react';

import { selectCurrentCourseId } from '../../db/courses/selectors';
import { selectCourseUserProfile } from '../../db/user-profiles/selectors';
import { useAppSelector } from '../../store/hooks';
import { CourseRole } from '../../types';

interface TitleWithEmailId {
  isEmailIdNeeded: true;
  isCourseRoleNeeded: false;
  title?: ReactNode | ((name?: string, emailId?: string) => ReactNode);
}

interface TitleWithCourseRole {
  isEmailIdNeeded: false;
  isCourseRoleNeeded: true;
  title?: ReactNode | ((name?: string, courseRole?: CourseRole) => ReactNode);
}

interface TitleWithEmailIdAndCourseRole {
  isEmailIdNeeded: true;
  isCourseRoleNeeded: true;
  title?: ReactNode | ((name?: string, emailId?: string, courseRole?: CourseRole) => ReactNode);
}

interface TitleWithoutEmailIdAndCourseRole {
  isEmailIdNeeded: false;
  isCourseRoleNeeded: false;
  title?: ReactNode | ((name?: string) => ReactNode);
}

export type UseCellTitle = {
  avatarId: MongoId;
} & (
  | TitleWithEmailId
  | TitleWithCourseRole
  | TitleWithEmailIdAndCourseRole
  | TitleWithoutEmailIdAndCourseRole
);

const useCellTitle = ({ avatarId, ...props }: UseCellTitle) => {
  const currentCourseId = useAppSelector((state) => selectCurrentCourseId(state));

  const courseUserProfile = useAppSelector((state) => selectCourseUserProfile(state, avatarId));
  const { name, emailId, courseUserDetails } = courseUserProfile || {};
  const courseRole = currentCourseId ? courseUserDetails?.[currentCourseId]?.role : undefined;

  if (!props.title) {
    return name;
  }

  if (typeof props.title !== 'function') {
    return props.title;
  }

  if (props.isEmailIdNeeded && props.isCourseRoleNeeded) {
    return props.title(name, emailId, courseRole);
  }

  if (props.isEmailIdNeeded) {
    return props.title(name, emailId);
  }

  if (props.isCourseRoleNeeded) {
    return props.title(name, courseRole);
  }

  return props.title(name);
};

export default useCellTitle;
